import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, OpenInNew, ContentCopy, Description } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderInfoQuery, useGetProviderTemplatesQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function TemplatesListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const perPageParam = searchParams.get('perPage');
    return perPageParam ? parseInt(perPageParam, 10) : 20;
  });
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initialFilters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'perPage') {
        initialFilters[key] = value;
      }
    });
    return initialFilters;
  });

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Add page if not 1
    if (page !== 1) {
      newParams.set('page', page.toString());
    }

    // Add perPage if not 20
    if (perPage !== 20) {
      newParams.set('perPage', perPage.toString());
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [page, perPage, filters, setSearchParams]);

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Get URL for each row
  const getRowUrl = useCallback((template: any) => {
    const templateType = template._templateType || 'user';
    return `/admin-v2/provider/${encodeURIComponent(providerId!)}/templates/${template.template_id}/${template.template_version}?type=${templateType}`;
  }, [providerId]);

  // Fetch provider info
  const { data: provider, isLoading: userLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  // Fetch templates for the provider with pagination and filtering
  const { data: templatesData, isLoading: templatesLoading } = useGetProviderTemplatesQuery(
    {
      providerId: providerId ?? '',
      page,
      pageSize: perPage,
      search: filters.search,
      filterByType: filters.filterByType as 'public' | 'user' | undefined,
      filterByLanguage: filters.filterByLanguage,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder as 'asc' | 'desc' | undefined,
    },
    { skip: !providerId },
  );

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const templatesColumns = [
    {
      key: 'template_name',
      label: 'Template Name',
      render: (value: string, row: any) => {
        // Try different name fields based on template type
        const displayName = row.public_name || row.template_name || value;
        return (
          <span className="text-sm font-medium text-gray-900">{displayName || 'Unnamed Template'}</span>
        );
      },
    },
    {
      key: 'template_id',
      label: 'Template ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 font-mono">
            {value ? (value.length > 20 ? value.substring(0, 20) + '...' : value) : 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(value);
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: '_templateType',
      label: 'Type',
      render: (value: string | undefined) => (
        <Badge color={value === 'public' ? 'primary' : value === 'user' ? 'secondary' : 'default'}>
          {value || 'user'}
        </Badge>
      ),
    },
    {
      key: 'template_version',
      label: 'Version',
      render: (value: number) => (
        <span className="text-sm text-gray-600">v{value || 1}</span>
      ),
    },
    {
      key: 'created_on',
      label: 'Created',
      render: (value: number) => (
        <span className="text-sm text-gray-600">
          {value ? formatDateTime(new Date(value * 1000).toISOString()) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'modified_on',
      label: 'Modified',
      render: (value: number) => (
        <span className="text-sm text-gray-600">
          {value ? formatDateTime(new Date(value * 1000).toISOString()) : 'N/A'}
        </span>
      ),
    },
  ];

  const filterConfigs = [
    {
      key: 'filterByType',
      label: 'Type',
      type: 'select' as const,
      options: [
        { value: 'public', label: 'Public' },
        { value: 'user', label: 'User' },
      ],
    },
    {
      key: 'filterByLanguage',
      label: 'Language',
      type: 'text' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {provider.name}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Templates</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Provider Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
                  <p className="text-gray-600">All templates for {provider.name}</p>
                  <p className="text-sm text-gray-500">Provider ID: {provider.provider_id}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Templates</div>
                <div className="text-2xl font-bold text-gray-900">
                  {templatesData?.total || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filterable Data Table */}
        <FilterableDataTable
          title="Provider Templates"
          columns={templatesColumns as any}
          data={templatesData?.data || []}
          total={templatesData?.total}
          loading={templatesLoading}
          page={page}
          perPage={perPage}
          filters={filterConfigs}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onFiltersChange={handleFiltersChange}
          getRowUrl={getRowUrl}
          emptyMessage="No templates found for this provider"
        />
      </main>
    </div>
  );
}